import { APP_URL } from '@/lib/appUrl';
import React, { useState, useEffect } from 'react';
import { api, leaguesAPI, traitsAPI } from '@/lib/api';
import { MemberProfileDialog } from './MemberProfileDialog';
import { PlayerTraitsRow } from './PlayerTraits';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import {
  Users, Link2, Unlink, Search, UserCheck, UserX, UserPlus,
  Trophy, Mail, AlertCircle, MessageCircle, Copy, Send, Info, Tag,
} from 'lucide-react';
import { getInitials } from '@/lib/utils';

export const LeagueMembersManager = ({ leagueId, leagueName, sportId, onMembersChange }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Add member dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addMode, setAddMode] = useState('registered');
  const [addQuery, setAddQuery] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  
  // Link dialog state
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedNewUser, setSelectedNewUser] = useState(null);
  const [linking, setLinking] = useState(false);
  
  // Invite dialog state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteMember, setInviteMember] = useState(null);

  // Profile (skill + traits) dialog state
  const [traitsCatalog, setTraitsCatalog] = useState([]);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [profileMember, setProfileMember] = useState(null);

  // Get app URL for invites
  const appUrl = APP_URL;

  const fetchMembers = async () => {
    try {
      const res = await api.get(`/leagues/${leagueId}/members`);
      const nextMembers = res.data.members || [];
      setMembers(nextMembers);
      onMembersChange?.(nextMembers.filter((member) => member.status === 'approved'));
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Errore nel caricamento dei membri');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await api.get(`/leagues/${leagueId}/available-users`);
      setAvailableUsers(res.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Errore nel caricamento degli utenti disponibili');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleOpenAddDialog = () => {
    setAddMode('registered');
    setAddQuery('');
    setSelectedNewUser(null);
    setShowAddDialog(true);
    fetchAvailableUsers();
  };

  const handleAddModeChange = (mode) => {
    setAddMode(mode);
    setAddQuery('');
    setSelectedNewUser(null);
  };

  const handleAddMember = async (event) => {
    event.preventDefault();
    const name = addQuery.trim();
    if (addMode === 'offline' ? !name : !selectedNewUser) return;

    setAddingMember(true);
    try {
      if (addMode === 'offline') {
        await api.post(`/leagues/${leagueId}/members`, { name });
      } else {
        await leaguesAPI.addMember(leagueId, selectedNewUser.user_id);
      }
      toast.success(`${addMode === 'offline' ? name : selectedNewUser.name} aggiunto alla lega`);
      setShowAddDialog(false);
      await fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta del membro');
    } finally {
      setAddingMember(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [leagueId]);

  useEffect(() => {
    traitsAPI.getAll(sportId)
      .then((res) => setTraitsCatalog(res.data.traits || []))
      .catch(() => {/* catalog non critico per il rendering base */});
  }, [sportId]);

  const handleOpenProfileDialog = (member) => {
    setProfileMember(member);
    setShowProfileDialog(true);
  };

  const renderMemberMeta = (member) => (
    <PlayerTraitsRow
      className="mt-1"
      size="md"
      traits={member.traits || []}
      skillLevel={member.skill_level}
      form={member.form}
      formBreakdown={member.attributes?.form_breakdown}
      preferredFoot={member.attributes?.preferred_foot}
      catalog={traitsCatalog}
    />
  );

  const handleOpenLinkDialog = (member) => {
    setSelectedMember(member);
    setSelectedNewUser(null);
    setShowLinkDialog(true);
    fetchAvailableUsers();
  };

  const handleLinkAccount = async () => {
    if (!selectedMember || !selectedNewUser) return;
    
    setLinking(true);
    try {
      await api.post(`/leagues/${leagueId}/members/${selectedMember.user_id}/link`, {
        new_user_id: selectedNewUser.user_id
      });
      toast.success(`Account collegato a ${selectedNewUser.name}`);
      setShowLinkDialog(false);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel collegamento');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkAccount = async (member) => {
    if (!confirm(`Sei sicuro di voler scollegare l'account di ${member.user?.name}? I dati storici verranno preservati.`)) {
      return;
    }
    
    try {
      await api.post(`/leagues/${leagueId}/members/${member.user_id}/unlink`);
      toast.success('Account scollegato');
      fetchMembers();
    } catch (error) {
      toast.error('Errore nello scollegamento');
    }
  };

  // Invite functions
  const handleOpenInvite = (member) => {
    setInviteMember(member);
    setShowInviteDialog(true);
  };

  const getInviteMessage = (memberName) => {
    return `Ciao ${memberName}! 👋

Sei stato aggiunto alla lega "${leagueName}" su LeagueHub! 🏆

Per collegare il tuo account e vedere le tue statistiche:
1. Vai su ${appUrl}
2. Clicca su "Accedi o registrati"
3. Una volta registrato, ti collegherò al tuo profilo

A presto in campo! ⚽`;
  };

  const handleShareWhatsApp = () => {
    if (!inviteMember) return;
    const message = getInviteMessage(inviteMember.user?.name?.split(' ')[0] || 'giocatore');
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    toast.success('Messaggio pronto per WhatsApp');
  };

  const handleCopyInvite = () => {
    if (!inviteMember) return;
    const message = getInviteMessage(inviteMember.user?.name?.split(' ')[0] || 'giocatore');
    navigator.clipboard.writeText(message);
    toast.success('Messaggio copiato!');
  };

  // Filter members by search
  const filteredMembers = members.filter(m => 
    m.user?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.user?.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const normalizedAddQuery = addQuery.trim().toLowerCase();
  const filteredAvailableUsers = availableUsers.filter((candidate) =>
    !normalizedAddQuery ||
    candidate.name?.toLowerCase().includes(normalizedAddQuery) ||
    candidate.email?.toLowerCase().includes(normalizedAddQuery)
  );

  // Separate placeholder and real members
  const placeholderMembers = filteredMembers.filter(m => m.is_placeholder);
  const realMembers = filteredMembers.filter(m => !m.is_placeholder);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">Caricamento membri...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Gestione Membri ({members.length})
            </CardTitle>
            <CardDescription>
              Aggiungi utenti registrati o giocatori senza account e collegali in seguito
            </CardDescription>
          </div>
          <Button className="h-11 w-full sm:h-9 sm:w-auto" onClick={handleOpenAddDialog}>
            <UserPlus className="h-4 w-4" />
            Aggiungi membro
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca membro..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Placeholder members (need linking) */}
        {placeholderMembers.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-orange-600">
              <AlertCircle className="h-4 w-4" />
              Da collegare ({placeholderMembers.length})
            </div>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Giocatore</TableHead>
                    <TableHead className="text-center">Punti</TableHead>
                    <TableHead className="text-center">Partite</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {placeholderMembers.map((member) => (
                    <TableRow key={member.user_id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={member.user?.picture} />
                            <AvatarFallback>{getInitials(member.user?.name)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{member.user?.name}</p>
                            <p className="text-xs text-orange-600">Account non collegato</p>
                            {renderMemberMeta(member)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">
                          {member.stats?.total_points || 0} pt
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm text-muted-foreground">
                        {member.stats?.matches_played || 0}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenProfileDialog(member)}
                            title="Modifica tag caratteristiche"
                          >
                            <Tag className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenInvite(member)}
                            title="Invita a registrarsi"
                          >
                            <MessageCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleOpenLinkDialog(member)}
                            className="bg-orange-500 hover:bg-orange-600"
                          >
                            <Link2 className="h-4 w-4 mr-1" />
                            Collega
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Real members (already linked) */}
        {realMembers.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-green-600">
              <UserCheck className="h-4 w-4" />
              Account collegati ({realMembers.length})
            </div>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Giocatore</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-center">Punti</TableHead>
                    <TableHead className="text-center">Ruolo</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {realMembers.map((member) => (
                    <TableRow key={member.user_id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={member.user?.picture} />
                            <AvatarFallback>{getInitials(member.user?.name)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <span className="font-medium">{member.user?.name}</span>
                            {renderMemberMeta(member)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {member.user?.email}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">
                          {member.stats?.total_points || 0} pt
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={member.role === 'admin' ? 'default' : 'outline'}>
                          {member.role === 'admin' ? 'Admin' : 'Giocatore'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenProfileDialog(member)}
                            title="Modifica tag caratteristiche"
                          >
                            <Tag className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUnlinkAccount(member)}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <Unlink className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {filteredMembers.length === 0 && (
          <p className="text-center text-muted-foreground py-4">
            Nessun membro trovato
          </p>
        )}

        {/* Add member dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Aggiungi membro
              </DialogTitle>
              <DialogDescription>
                Scegli se aggiungere un utente registrato o un giocatore senza account.
              </DialogDescription>
            </DialogHeader>

            <form className="space-y-4" onSubmit={handleAddMember}>
              <div
                className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                role="group"
                aria-label="Tipo di giocatore"
              >
                <Button
                  type="button"
                  variant={addMode === 'registered' ? 'default' : 'outline'}
                  aria-pressed={addMode === 'registered'}
                  className="min-h-11 h-auto whitespace-normal"
                  onClick={() => handleAddModeChange('registered')}
                >
                  <UserCheck className="h-4 w-4" aria-hidden="true" />
                  Utente registrato
                </Button>
                <Button
                  type="button"
                  variant={addMode === 'offline' ? 'default' : 'outline'}
                  aria-pressed={addMode === 'offline'}
                  className="min-h-11 h-auto whitespace-normal"
                  onClick={() => handleAddModeChange('offline')}
                >
                  <UserPlus className="h-4 w-4" aria-hidden="true" />
                  Giocatore senza account
                </Button>
              </div>

              {addMode === 'offline' ? (
                <div className="space-y-1.5">
                  <Label htmlFor="offline-player-name">Nome e cognome</Label>
                  <Input
                    id="offline-player-name"
                    autoFocus
                    autoComplete="name"
                    maxLength={100}
                    required
                    value={addQuery}
                    onChange={(event) => setAddQuery(event.target.value)}
                    placeholder="Es. Mario Rossi"
                    aria-describedby="offline-player-help"
                  />
                  <p id="offline-player-help" className="text-xs text-muted-foreground">
                    Potrai collegare un account in seguito senza perdere dati e statistiche.
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="add-member-search">Nome o email</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                      <Input
                        id="add-member-search"
                        autoFocus
                        autoComplete="off"
                        value={addQuery}
                        onChange={(event) => {
                          setAddQuery(event.target.value);
                          setSelectedNewUser(null);
                        }}
                        placeholder="Cerca nome o email"
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div aria-live="polite">
                    {loadingUsers ? (
                      <p className="py-6 text-center text-sm text-muted-foreground">Ricerca in corso...</p>
                    ) : filteredAvailableUsers.length === 0 ? (
                      <p className="py-6 text-center text-sm text-muted-foreground">Nessun utente disponibile</p>
                    ) : (
                      <ScrollArea className="h-56 rounded-lg border">
                        <ul className="space-y-1 p-2" aria-label="Utenti disponibili">
                          {filteredAvailableUsers.map((candidate) => {
                            const selected = selectedNewUser?.user_id === candidate.user_id;
                            return (
                              <li key={candidate.user_id}>
                                <button
                                  type="button"
                                  aria-pressed={selected}
                                  onClick={() => setSelectedNewUser(candidate)}
                                  className={`flex min-h-11 w-full items-center gap-3 rounded-lg p-2 text-left transition-colors ${
                                    selected ? 'bg-primary/10 ring-2 ring-primary' : 'hover:bg-muted'
                                  }`}
                                >
                                  <Avatar className="h-9 w-9">
                                    <AvatarImage src={candidate.picture} />
                                    <AvatarFallback>{getInitials(candidate.name)}</AvatarFallback>
                                  </Avatar>
                                  <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-medium">{candidate.name}</span>
                                    <span className="block truncate text-xs text-muted-foreground">{candidate.email}</span>
                                  </span>
                                  {selected && <UserCheck className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </ScrollArea>
                    )}
                  </div>
                </>
              )}

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} disabled={addingMember}>
                  Annulla
                </Button>
                <Button
                  type="submit"
                  disabled={addingMember || (addMode === 'offline' ? !addQuery.trim() : !selectedNewUser)}
                >
                  {addingMember ? 'Aggiunta...' : addMode === 'offline' ? 'Aggiungi giocatore' : 'Conferma aggiunta'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Link Account Dialog */}
        <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="h-5 w-5" />
                Collega Account
              </DialogTitle>
              <DialogDescription>
                Seleziona l&apos;utente registrato da collegare a <strong>{selectedMember?.user?.name}</strong>
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              {/* Current member info */}
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg mb-4">
                <Avatar>
                  <AvatarImage src={selectedMember?.user?.picture} />
                  <AvatarFallback>{getInitials(selectedMember?.user?.name)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{selectedMember?.user?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedMember?.stats?.total_points || 0} punti • {selectedMember?.stats?.matches_played || 0} partite
                  </p>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex justify-center my-2">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Link2 className="h-4 w-4 text-primary" />
                </div>
              </div>

              {/* Available users list */}
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">Utenti registrati disponibili:</p>
                {loadingUsers ? (
                  <p className="text-muted-foreground text-center py-4">Caricamento...</p>
                ) : availableUsers.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground">
                    <UserX className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Nessun utente disponibile</p>
                    <p className="text-xs mt-1">Gli utenti devono prima registrarsi</p>
                  </div>
                ) : (
                  <ScrollArea className="h-48 border rounded-lg">
                    <div className="p-2 space-y-1">
                      {availableUsers.map((u) => (
                        <button
                          key={u.user_id}
                          type="button"
                          onClick={() => setSelectedNewUser(u)}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                            selectedNewUser?.user_id === u.user_id 
                              ? 'bg-primary/10 border-2 border-primary' 
                              : 'hover:bg-muted'
                          }`}
                        >
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={u.picture} />
                            <AvatarFallback>{getInitials(u.name)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 text-left">
                            <p className="font-medium text-sm">{u.name}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                          {selectedNewUser?.user_id === u.user_id && (
                            <UserCheck className="h-5 w-5 text-primary" />
                          )}
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLinkDialog(false)}>
                Annulla
              </Button>
              <Button 
                onClick={handleLinkAccount} 
                disabled={!selectedNewUser || linking}
              >
                {linking ? 'Collegamento...' : 'Collega Account'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Member Profile Dialog (skill + traits) */}
        <MemberProfileDialog
          sportId={sportId}
          open={showProfileDialog}
          onOpenChange={setShowProfileDialog}
          leagueId={leagueId}
          member={profileMember}
          onSaved={fetchMembers}
        />

        {/* Invite Dialog */}
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Invita {inviteMember?.user?.name?.split(' ')[0]}
              </DialogTitle>
              <DialogDescription>
                Invia un messaggio per invitare il giocatore a registrarsi sull&apos;app
              </DialogDescription>
            </DialogHeader>

            <div className="py-4 space-y-4">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>Come funziona:</strong>
                  <ol className="mt-2 ml-4 list-decimal text-sm space-y-1">
                    <li>Invia il messaggio al giocatore</li>
                    <li>Il giocatore clicca sul link e accede o si registra</li>
                    <li>Torna qui e clicca &quot;Collega&quot; per associarlo</li>
                  </ol>
                </AlertDescription>
              </Alert>

              <div className="p-3 bg-muted rounded-lg text-sm whitespace-pre-wrap">
                {inviteMember && getInviteMessage(inviteMember.user?.name?.split(' ')[0] || 'giocatore')}
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={handleCopyInvite} className="w-full sm:w-auto">
                <Copy className="h-4 w-4 mr-2" />
                Copia Messaggio
              </Button>
              <Button 
                onClick={handleShareWhatsApp} 
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Invia su WhatsApp
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Info note */}
        {placeholderMembers.length > 0 && (
          <Alert className="mt-4">
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>Per collegare un giocatore:</strong> Il giocatore deve prima registrarsi su LeagueHub 
              (anche solo facendo login dalla homepage). Poi potrai collegarlo qui.
              Usa il pulsante <MessageCircle className="h-3 w-3 inline mx-1" /> per invitarlo.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default LeagueMembersManager;
